import { fmt } from '@/features/notas/calculo';
import type { Estatistica } from '@/features/notas/types';
import { Alvo, AreaGrafico, COR } from './base';

export interface LinhaFaixa {
  rotulo: string;
  estat: Estatistica;
}

/**
 * Um trilho de 0 a 10 por componente: a faixa do meio da turma (do 1º ao 3º quartil), o traço da mediana e
 * o ponto do aluno. Mostra onde ele está sem revelar a nota de ninguém.
 */
export function FaixasTurma({ linhas }: { linhas: LinhaFaixa[] }) {
  const alt = 46;
  const altura = linhas.length * alt + 30;
  return (
    <AreaGrafico altura={altura} rotulo="Sua nota em cada componente comparada à turma">
      {(w) => {
        const esq = Math.min(130, w * 0.32);
        const dir = 64;
        const larg = w - esq - dir;
        const x = (v: number) => esq + (Math.max(0, Math.min(10, v)) / 10) * larg;
        return (
          <g>
            {[0, 2, 4, 6, 8, 10].map((t) => (
              <g key={t}>
                <line x1={x(t)} x2={x(t)} y1={6} y2={altura - 22} stroke={COR.grade} strokeWidth={1} />
                <text x={x(t)} y={altura - 6} textAnchor="middle" fontSize={11} fill={COR.textoFraco}>
                  {t}
                </text>
              </g>
            ))}
            {linhas.map(({ rotulo, estat }, i) => {
              const cy = 10 + i * alt + alt / 2;
              const v = estat.valorAluno;
              return (
                <Alvo
                  key={rotulo}
                  titulo={
                    <>
                      <strong>{rotulo}</strong>
                      <br />
                      Você: {fmt(v)} · mediana da turma: {fmt(estat.mediana)}
                      <br />
                      Metade da turma ficou entre {fmt(estat.q1)} e {fmt(estat.q3)}
                      {estat.percentil !== null && (
                        <>
                          <br />
                          Você está acima de {Math.round(estat.percentil)}% da turma
                        </>
                      )}
                    </>
                  }
                >
                  <g>
                    <rect x={0} y={cy - alt / 2} width={w} height={alt} fill="transparent" />
                    <text x={0} y={cy} dy="0.32em" fontSize={13} fill={COR.texto}>
                      {rotulo}
                    </text>
                    <line x1={x(0)} x2={x(10)} y1={cy} y2={cy} stroke={COR.trilho} strokeWidth={8} strokeLinecap="round" />
                    <line x1={x(estat.q1)} x2={x(estat.q3)} y1={cy} y2={cy} stroke={COR.turma} strokeWidth={8} strokeLinecap="round" />
                    <line x1={x(estat.mediana)} x2={x(estat.mediana)} y1={cy - 9} y2={cy + 9} stroke={COR.turmaForte} strokeWidth={2} />
                    {v !== null && (
                      <circle cx={x(v)} cy={cy} r={7} fill={COR.voce} stroke={COR.superficie} strokeWidth={2} />
                    )}
                    <text x={w} y={cy} dy="0.32em" textAnchor="end" fontSize={13} fontWeight={700} fill={COR.texto}>
                      {fmt(v)}
                    </text>
                  </g>
                </Alvo>
              );
            })}
          </g>
        );
      }}
    </AreaGrafico>
  );
}
